<template>
  <v-container
    fluid
    style="margin: 0; padding: 0; position: relative"
    @drop="handleFileDrop"
  >
    <div v-if="imageMode">
      <v-img
        class="image"
        contain
        :src="loadedImage"
      />
    </div>
    <div
      v-if="!readonly && userHasUploadRight"
      ref="dragbox"
      :class="['file-wrapper', isOver ? 'isOver' : '']"
    >
      <input
        id="file-input"
        type="file"
        name="file-input"
        :multiple="imageMode || singleFile ? 'false' : 'true'"
        @change="handleFileInput"
      />
      <span
        v-if="!imageMode"
        class="ml-2"
      >{{ fileInputLabel }}</span>
      <div
        v-if="imageMode"
        class="d-flex align-center justify-space-between"
      >
        <span class="ml-2">{{ fileInputLabel }}</span>
        <w-download-button
          v-if="value && userHasEditFileRight && (value.data.exists || value.isNew)"
          color="default darken-2"
          @click="handleFileDownload(value)"
        >
          <template #tooltip>
            {{ $t('Components.sentences.downloadImage') }}
          </template>
        </w-download-button>
        <w-camera-button
          v-if="showMobileCamera"
          color="default darken-2"
          class="mr-2"
          @click="invokeCameraAction"
        />
        <w-delete-button
          v-if="value && userHasEditFileRight"
          class="delete-btn"
          color="default darken-2"
          @click="removeFileImgMode"
        >
          <template #tooltip>
            {{
              imageMode
                ? $t('Components.sentences.removeImage')
                : $t('Components.sentences.removeFile')
            }}
          </template>
        </w-delete-button>
      </div>
    </div>
    <div v-if="singleFile && value">
      <UploadZoneFileItem
        :file="value"
        :readonly="readonly"
        :user-has-edit-file-right="userHasEditFileRight"
        @removeFile="removeFile"
        @downloadFile="handleFileDownload"
      />
    </div>
    <w-list v-if="!imageMode && !singleFile">
      <v-list-item
        v-for="file in value"
        :key="file.pathName"
        class="pa-0"
      >
        <UploadZoneFileItem
          :file="file"
          :readonly="readonly"
          :user-has-edit-file-right="userHasEditFileRight"
          @removeFile="removeFile"
          @downloadFile="handleFileDownload"
        />
      </v-list-item>
    </w-list>
  </v-container>
</template>

<script>
// components
import UploadZoneFileItem from './UploadZoneFileItem.vue';
// mixins
import dataMixin from '@/mixins/data-mixin';
import isRequired from '@/mixins/component/is-required-mixin';
import mobileCameraMixin from '@/mixins/mobile_wrapper/mobile-camera-mixin';
import rightMixin from '@/mixins/right-mixin';
// services
import base64ToFile from "@/services/base64-service";
import fileUploadService from "@/services/file-upload-service";
import { invokeGetFileFromServer } from '@/services/invoke-get-file-service';
// utils
import cloneDeep from 'lodash/cloneDeep';
import filesUtils from '@/utils/files-utils';
import { mapActions } from 'vuex';
import SNACKBAR_TYPES from '@/utils/enum/SNACKBAR_TYPES';

const DEFAULT_IMAGE = require('@/assets/upload/default_profil_image.jpg');

export default {
  name: 'WorkUploadZone',
  components: {
    UploadZoneFileItem,
  },
  props: {
    value: {
      type: [Object, Array],
      default: undefined,
    },
    index: { type: [String, Number], default: undefined },
    readonly: { type: Boolean, default: false },
    imageMode: { type: Boolean, default: false },
    fileSizeCheck: { type: Boolean, default: false },
    singleFile: { type: Boolean, default: false },
    showMobileCamera: { type: Boolean, default: true },
  },
  mixins: [
    isRequired,
    dataMixin('tempFiles', self => self.index ? `transientData.tempFiles.${self.index}` : 'transientData.tempFiles', []),
    dataMixin('mobileFiles', 'transientData.mobileFiles'),
    mobileCameraMixin(self => self.handleAddMobileFiles, 'transientData.mobileFiles', undefined, self => self.imageMode),
    rightMixin(
      'userHasCreateFileRight',
      'WORK_Files_Anlegen',
      'configurations/files'
    ),
    rightMixin(
      'userHasEditFileRight',
      'WORK_Files_Schreibend',
      'configurations/files'
    ),
  ],
  data() {
    return {
      isOver: false,
      allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif'],
      allowedImageFileExtensions: ['jpg', 'jpeg', 'png', 'gif'],
      loadedImage: DEFAULT_IMAGE,
    };
  },
  mounted() {
    if (this.tempFiles == undefined) this.tempFiles = [];
    else if (this.tempFiles.length > 0) {
      this.loadImagePreview();
    }

    if (this.value != undefined) {
      this.initializeTempData();
    } else {
      this.tempFiles = [];
      this.loadedImage = DEFAULT_IMAGE;
    }

    this.$refs?.dragbox?.addEventListener('dragover', this.setIsOverToTrue);
    this.$refs?.dragbox?.addEventListener('dragleave', this.setIsOverToFalse);
  },
  computed: {
    userHasUploadRight() {
      return this.userHasEditFileRight || this.userHasCreateFileRight
    },
    fileInputLabel() {
      let label = ''
      if (this.imageMode)
        label = this.$t('Components.sentences.changeImage');
      else if (this.singleFile)
        label = this.$t('Components.sentences.uploadDocument');
      else
        label = this.$t('Components.sentences.uploadDocuments');

      return `${label}${this.required ? '*' : ''}`;
    }
  },
  watch: {
    value() {
      this.initializeTempData();
    },
    mobileFiles(files) {
      if (files === undefined || files.length === 0)
        return;

      if (!this.validateFiles(files))
        return (this.mobileFiles = []);

      if (this.imageMode || this.singleFile) {
        let file = files[0];
        this.tempFiles = [
          { data: { resource: file.data.resource }, id: file.id, pathName: file.data },
        ];
        this.emitInput({ data: file.data });
        this.loadImagePreview();
      } else {
        this.tempFiles = [
          ...this.tempFiles,
          files.map((file) => ({
            data: { resource: file.data.resource },
            id: file.id,
            pathName: file.data,
          })),
        ];
        this.emitInput([...this.files, files]);
      }
      this.mobileFiles = [];
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  deactivated() {
    this.cleanup();
  },
  methods: {
    ...mapActions('globalSnackbarStore', ['showSnackbarWithMessage']),
    setIsOverToTrue() {
      this.isOver = true;
    },
    setIsOverToFalse() {
      this.isOver = false;
    },
    async handleAddMobileFiles(files) {
      if (!files)
        return;

      let mobileFiles = Array.isArray(files) ? files : [files];
      let newFiles = await Promise.all(mobileFiles.map(async (mobileFile) => {
        let file = await base64ToFile(mobileFile);
        let response = await fileUploadService.postFile(file);
        let newFile = fileUploadService.getNewFileForResponse(response.data, file);
        return newFile;
      }));

      this.mobileFiles = newFiles;
    },
    handleFileDrop(e) {
      this.handleFiles(e.dataTransfer.files);
    },
    handleFileInput(e) {
      this.handleFiles(e.target.files);
    },
    validateFiles(files) {
      let isImageTypeValid = files.every(
        (f) => this.validateFileType(f.data?.mimeType, f.data?.resource?.name)
      );
      return this.validateFileSize(files) && isImageTypeValid;
    },
    validateFileType(type, name) {
      return this.allowedImageTypes.includes(type?.toLowerCase()) &&
        this.allowedImageFileExtensions.includes(name?.split('.').pop().toLowerCase())
    },
    validateFileSize(files) {
      return !this.fileSizeCheck ||
        (this.fileSizeCheck && filesUtils.fileSizeCheck(files))
    },
    handleFiles(files) {
      if (!this.validateFileSize(files))
        return;

      this.storeFiles(files);
    },
    storeFiles(files) {
      if (!files)
        return;

      [...files]
        .filter(f => this.tempFiles.find(x => x.pathName === f.pathName) === undefined)
        .forEach((f) => {
          if (this.imageMode && !this.validateFileType(f.type, f.name)) {
            this.showSnackbarWithMessage({
              type: SNACKBAR_TYPES.ERROR,
              message: this.$t('Components.sentences.allowedImageTypes'),
            });
            return;
          }

          this.submitFile(f);
        });
      this.isOver = false;
    },
    removeFileImgMode() {
      try {
        document.getElementById('file-input').value = '';
        // eslint-disable-next-line no-empty
      } catch (e) {
      } finally {
        this.resetFiles();
      }
    },
    resetFiles() {
      this.emitInput(undefined);
      this.tempFiles = [];
      this.loadedImage = DEFAULT_IMAGE;
    },
    removeFile(file) {
      const pathName = file?.data?.pathName;
      if (this.singleFile) {
        this.emitInput(undefined);
        return;
      }
      var removableIndex = this.tempFiles.findIndex(
        (tf) => tf.pathName == pathName
      );
      if (removableIndex >= 0) {
        var tempFilesCopy = cloneDeep(this.tempFiles);
        tempFilesCopy.splice(removableIndex, 1);
        this.tempFiles = tempFilesCopy;
      }

      removableIndex = this.value.findIndex(
        (f) => f.data.pathName == pathName || f.data == pathName
      );
      if (removableIndex >= 0) {
        var filesCopy = cloneDeep(this.value);
        filesCopy.splice(removableIndex, 1);
        this.emitInput(filesCopy);
      }
    },
    emitInput(value) {
      return this.$emit('input', value);
    },
    async submitFile(file) {
      const response = await fileUploadService.postFile(file);
      const postedFileData = response.data;
      const newFile = {
        data: postedFileData,
        isNew: true,
      }
      const newTempFile = {
        data: { resource: file },
        id: undefined,
        pathName: postedFileData.pathName,
      }

      if (this.imageMode || this.singleFile) {
        this.emitInput(newFile);
        this.tempFiles = [newTempFile];
        this.loadImagePreview();
      } else {
        this.emitInput([...(this.value ?? []), newFile]);
        this.tempFiles = [
          ...this.tempFiles,
          newTempFile,
        ];
      }
    },
    async handleFileDownload(file) {
      const response = await invokeGetFileFromServer(file);
      filesUtils.downloadFile(
        response.data,
        file.data.originalName
      );
    },
    loadImagePreview() {
      var reader = new FileReader();
      reader.onload = (e) => {
        this.loadedImage = e.target.result;
      };
      reader.readAsDataURL(this.tempFiles[0].data.resource);
    },
    async initializeTempDataItem(file) {
      let response;
      try {
        response = await invokeGetFileFromServer(file);
      } catch (error) {
        console.error(error);
        return;
      }

      var loadedFile = {
        data: {
          resource: new File([response.data], file.data.pathName, {
            type: file.data.mimeType,
          })
        },
        id: file.id,
        pathName: file.data.pathName,
      };

      if (this.imageMode) {
        this.tempFiles = [loadedFile];
        this.loadImagePreview();
      } else {
        this.tempFiles = [...this.tempFiles, loadedFile];
      }
    },
    initializeTempData() {
      // If we are not in image mode, handle files as array
      if (this.value == undefined) {
        this.resetFiles();
        return;
      }

      if (this.imageMode || this.singleFile) {
        this.initializeTempDataItem(this.value);
        return;
      }

      this.value
        .filter((f) => f.id !== undefined)
        .filter((f) => !this.tempFiles.find((tf) => tf.pathName === f.data.pathName))
        .forEach(this.initializeTempDataItem);
    },
    cleanup() {
      this.$refs.dragbox?.removeEventListener('dragover', this.setIsOverToTrue);
      this.$refs.dragbox?.removeEventListener('dragleave', this.setIsOverToFalse);
    },
  },
};
</script>

<style lang="scss" scoped>
.file-wrapper {
  text-align: center;
  //height: 6em;
  border-radius: 0.313rem;
  vertical-align: middle;
  // display: table-cell;
  position: relative;
  // width: 800px;
  overflow: hidden;
  background: lightgrey;
  margin: 0;
  padding: 0;
  color: var(--v-primary-base);
}

.file-wrapper.isOver {
  background: gray;
}

.file-wrapper input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  cursor: pointer;
  opacity: 0;
  filter: alpha(opacity=0);
  height: 100%;
  // height: 6em;
}

.delete-btn {
  position: absolute;
  z-index: 1;
  right: 0;
}

.image {
  max-height: fit-content;
  max-width: fit-content;
}
</style>
